import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle, FileWarning, RefreshCw } from 'lucide-react';
import { axiosInstance } from '../App';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from './ui/sheet';

const FILTERS = [
  { code: 'all', label: 'All' },
  { code: 'refresh_failed', label: 'Refresh failed' },
  { code: 'expired', label: 'Past end date' },
  { code: 'review_due', label: 'Review due' },
  { code: 'not_reviewed', label: 'Not reviewed' },
  { code: 'no_owner', label: 'No owner' },
  { code: 'never_used', label: 'Never used' },
];

// Knowledge that needs a Source Leader's or Tenant Admin's attention. Renders
// nothing for anyone else, so ordinary members never see maintenance noise.
const KnowledgeAttentionPanel = ({ resolutions, onEdit, onChanged, openInitially = false }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('all');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: body } = await axiosInstance.get('/knowledge-attention');
      setData(body);
      return body;
    } catch {
      setData(null);
      return null;
    }
  }, []);

  useEffect(() => {
    load().then((body) => { if (body && openInitially) setOpen(true); });
  }, [load, openInitially]);

  const items = useMemo(() => (data?.items || []).filter((item) => filter === 'all' || item.reasons.some((reason) => reason.code === filter)), [data, filter]);
  if (!data) return null;

  const run = async (item, request, success) => {
    setBusyId(item.id);
    try {
      await request();
      toast.success(success);
      await load();
      onChanged?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'That did not work. Try again.');
    } finally { setBusyId(null); }
  };
  const edit = (item) => {
    const full = resolutions.find((candidate) => candidate.id === item.id);
    if (!full) { toast.error('Open the item from its folder to edit it.'); return; }
    setOpen(false);
    onEdit(full);
  };

  return <>
    <Button variant="outline" onClick={() => setOpen(true)} data-testid="knowledge-attention-button">
      <FileWarning size={15} className="mr-2" />Needs attention
      <span className={`ml-2 rounded-full px-2 text-xs font-bold ${data.counts.total ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600'}`}>{data.counts.total}</span>
    </Button>
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-lg" data-testid="knowledge-attention-panel">
        <SheetHeader>
          <SheetTitle>Knowledge that needs attention</SheetTitle>
          <SheetDescription>{data.scope === 'organisation' ? 'All knowledge in your organisation.' : `Knowledge in the teams you're Source Leader for${data.teams.length ? `: ${data.teams.map((team) => team.name).join(', ')}` : ''}.`}</SheetDescription>
        </SheetHeader>
        <div className="mt-4 flex flex-wrap gap-1.5" role="group" aria-label="Filter by reason">
          {FILTERS.filter((option) => option.code === 'all' || data.counts[option.code]).map((option) => <button key={option.code} type="button" aria-pressed={filter === option.code} onClick={() => setFilter(option.code)}
            className={`rounded-full border px-3 py-1 text-xs font-semibold ${filter === option.code ? 'border-sky-600 bg-sky-50 text-sky-700' : 'border-gray-200 bg-white text-gray-600'}`}>
            {option.label} {option.code === 'all' ? data.counts.total : data.counts[option.code]}
          </button>)}
        </div>
        {items.length ? <ul className="mt-4 divide-y divide-gray-100">
          {items.map((item) => <li key={item.id} className="py-3">
            <p className="text-sm font-semibold text-gray-900">{item.title}</p>
            <p className="text-xs text-gray-500">{[item.team_name || 'No team', item.updated_at ? `Updated ${new Date(item.updated_at).toLocaleDateString()}` : null].filter(Boolean).join(' · ')}</p>
            <ul className="mt-1.5 space-y-0.5">{item.reasons.map((reason) => <li key={reason.code} className="text-xs text-amber-800"><span className="font-semibold">{reason.label}.</span> <span className="text-gray-600">{reason.detail}</span></li>)}</ul>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => edit(item)}>Edit</Button>
              {item.reasons.some((reason) => reason.code === 'refresh_failed') && <Button size="sm" variant="outline" disabled={busyId === item.id}
                onClick={() => run(item, () => axiosInstance.post(`/resolutions/${item.id}/refresh-linked`), 'Refreshed')}><RefreshCw size={13} className="mr-1.5" />Retry refresh</Button>}
              {item.reasons.some((reason) => ['not_reviewed', 'review_due'].includes(reason.code)) && <Button size="sm" variant="outline" disabled={busyId === item.id}
                onClick={() => run(item, () => axiosInstance.post(`/knowledge-attention/${item.id}/reviewed`), 'Marked as reviewed')}><CheckCircle size={13} className="mr-1.5" />Still correct</Button>}
            </div>
          </li>)}
        </ul> : <p className="mt-6 flex items-center gap-2 text-sm text-gray-500"><CheckCircle size={16} className="text-emerald-600" aria-hidden="true" />{data.counts.total ? 'Nothing matches this filter.' : 'Everything you look after is in good shape.'}</p>}
      </SheetContent>
    </Sheet>
  </>;
};

export default KnowledgeAttentionPanel;
